import { darken, lighten, readableColor, rgba } from 'polished';
import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled, { createGlobalStyle, keyframes, ThemeContext } from 'styled-components';
import { Theme } from '../../../contexts/operator/operator-context';
import { TSite } from '../../../contexts/site/site-context';
import Content from './components/Content';
import Header from './components/Header';
import Navbar from './components/Navbar';
import dropArrow from '../../../assets/images/drop-arrow.svg';
import useScrollPosition from "../../../hooks/useScrollPosition";
import { setCspContent } from '../../../csp';
import useOperatorContext from "../../../contexts/operator/useOperatorContext";

type AppLayoutProps = {
    children: JSX.Element | JSX.Element[];
    page: TPage;
    dineInSite?: TSite;
};

export type TPage = 'channels' | 'order-at-table' | 'sites' | 'menu' | 'menu-new' | 'checkout' | 'confirmation' | null;

let metaScriptLoaded = false;

const AppLayout = ({ children, page, dineInSite }: AppLayoutProps) => {
    const themeContext = useContext(ThemeContext);
    const operatorContext = useOperatorContext();
    const scrollPosition = useScrollPosition();

    useEffect(() => {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', `${themeContext.v4.layout_1}`);
        document.querySelector('meta[rel="icon"]')?.setAttribute('href', `${themeContext.v4.favicon}`);
    }, [themeContext.v4.layout_1, themeContext.v4.favicon]);

	useEffect(() => {
    	if (
        	navigator.doNotTrack === "1" ||
        	navigator.globalPrivacyControl ||
        	!operatorContext.operator?.meta_tag ||
        	metaScriptLoaded
    	) {
        	return;
    	}

    	metaScriptLoaded = true;

    	const script = document.createElement('script');
    	script.id = 'meta';
    	script.append(`!function(f,b,e,v,n,t,s) {if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${operatorContext.operator?.meta_tag}');fbq('track', 'PageView');`);
    	document.head.appendChild(script);

    	console.log('Enabled for Meta Pixel');
	}, [operatorContext.operator?.meta_tag]);

    return (
        <div className={`page-${page}`}>
            <Helmet>
                <link rel="icon" type="image/png" href={themeContext.v4.favicon} />
                <link href="https://fonts.googleapis.com" rel="preconnect" />
                <link href="https://fonts.gstatic.com" rel="preconnect" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&amp;display=swap" rel="stylesheet" />
                <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:FILL@0..1&amp;display=block" rel="stylesheet" />

                <meta httpEquiv="Content-Security-Policy" content={setCspContent()} />
                <meta httpEquiv="Reporting-Endpoints" content='default="https://orderswift.report-uri.com/a/d/g"' />
                <meta httpEquiv="Report-To" content='{"group":"default","max_age":31536000,"endpoints":[{"url":"https://orderswift.report-uri.com/a/d/g"}],"include_subdomains":true}' />
                <meta httpEquiv="NEL" content='{"report_to":"default","max_age":31536000,"include_subdomains":true}' />
                <meta name="referrer" content="strict-origin-when-cross-origin" />
                <meta httpEquiv="Permissions-Policy" content="payment=(self), geolocation=(self)"/>
            </Helmet>
            <Navbar page={page} dineInSite={dineInSite} />
            <Header page={page}>
                <GlobalStyle theme={themeContext} />
            </Header>
            <Content page={page}>
                <>{children}</>
            </Content>
        </div>
    );
};

// Fixed colours for the Bootstrap-style form controls below. They used to come from
// legacy theme fields that the API no longer sends, so these were always the defaults.
const BORDER_COLOR = '#E7E7E7';
const FOCUS_COLOR = '#306FDB';
const PRIMARY_COLOR = '#EB5556';

const GlobalStyle = createGlobalStyle<{ theme: Theme }>`
    /* Variables from theme */

    :root {
        --logo:           url('${(props) => props.theme.v4.logo}');
        --logo_height:    32px;
        --layouts_hero:   url('${(props) => props.theme.v4.hero}');
        --layouts_hero_2: url('${(props) => props.theme.v4.hero_2}');

        --layouts_header_bg: ${(props) => props.theme.v4.layout_1};
        --layouts_nav_bg: ${(props) => props.theme.v4.layout_2};
        --layouts_nav_bg_opa0: ${(props) => props.theme.v4.layout_2 + '00'};
        --layouts_nav_border: ${(props) => `${props.theme.v4.layout_2_border_width} solid ${props.theme.v4.layout_2_border}`};
        --layouts_footer_bg: ${(props) => props.theme.v4.layout_3};
        --layouts_footer_border: ${(props) => `${props.theme.v4.layout_3_border_width} solid ${props.theme.v4.layout_3_border}`};
        --layouts_container_bg: ${(props) => props.theme.v4.layout_8};
        --layouts_container_border: ${(props) => props.theme.v4.layout_8_border};

        --layouts_body_channel_select_bg: ${(props) => props.theme.v4.layout_4_type === 'color' ? props.theme.v4.layout_4 : `url('${props.theme.v4.bg_tile}')`};
        --layouts_body_menu_bg: ${(props) => props.theme.v4.layout_5_type === 'color' ? props.theme.v4.layout_5 : `url('${props.theme.v4.bg_tile}')`};
        --layouts_body_payment_bg: ${(props) => props.theme.v4.layout_6_type === 'color' ? props.theme.v4.layout_6 : `url('${props.theme.v4.bg_tile}')`};
        --layouts_body_confirmation_bg: ${(props) => props.theme.v4.layout_7_type === 'color' ? props.theme.v4.layout_7 : `url('${props.theme.v4.bg_tile}')`};
        --layouts_body_dine_in_bg: ${(props) => props.theme.v4.layout_9_type === 'color' ? props.theme.v4.layout_9 : `url('${props.theme.v4.bg_tile}')`};

        --ui_nav_channels: ${(props) => props.theme.v4.ui_10};
        --ui_nav_channels_pill_label: ${(props) => props.theme.v4.ui_11};
        --ui_nav_channels_pill_inactive_label: ${(props) => props.theme.v4.ui_16};
        --ui_nav_channels_border: ${(props) => `${props.theme.v4.navigation_border} solid ${props.theme.v4.ui_12}`};
        --ui_nav_channels_underline_label: ${(props) => props.theme.v4.ui_17};
        --ui_nav_channels_underline_inactive_label: ${(props) => props.theme.v4.ui_18};
        --ui_nav_channels_underline: ${(props) => `${props.theme.v4.underline_border} solid ${props.theme.v4.ui_13}`};

        --ui_primary_cta: ${(props) => props.theme.v4.ui_1};
        --ui_primary_cta_label: ${(props) => props.theme.v4.ui_2};
        --ui_primary_cta_border: ${(props) => `${props.theme.v4.primary_cta_border} solid ${props.theme.v4.ui_3}`};

        --ui_card_border: ${(props) => `${props.theme.v4.card_border} solid ${props.theme.v4.ui_8}`};

        --ui_input_bg: ${(props) => props.theme.v4.ui_19};
        --ui_input_border: ${(props) => `2px solid ${props.theme.v4.ui_20}`};
        --ui_input_border_inactive: ${(props) => `1px solid ${props.theme.v4.ui_23 ?? '#F3F3F3'}`};

        --ui_banner_bg: ${(props) => props.theme.v4.ui_21};
        --ui_banner_color: ${(props) => props.theme.v4.ui_22};

        --text_heading_font: ${(props) => props.theme.v4.primary_font_regular ? 'OSPrimaryFont' : (props.theme.v4.secondary_font_regular ? 'OSSecondaryFont' : 'DM Sans')};
        --text_heading_font_spacing: ${(props) => props.theme.v4.primary_font_spacing};
        --text_heading_font_case: ${(props) => props.theme.v4.primary_font_case};
        --text_body_font: ${(props) => props.theme.v4.secondary_font_regular ? 'OSSecondaryFont' : (props.theme.v4.primary_font_regular ? 'OSPrimaryFont' : 'DM Sans')};
        --text_body_font_spacing: ${(props) => props.theme.v4.secondary_font_spacing};
        --text_body_font_case: ${(props) => props.theme.v4.secondary_font_case};

        --text_slogan_color: ${(props) => props.theme.v4.text_xl_header};
        --text_heading_color: ${(props) => props.theme.v4.text_l_header};
        --text_subheading_color: ${(props) => props.theme.v4.text_m_header};
        --text_subsubheading_color: ${(props) => props.theme.v4.text_s_header};
        --text_body_color: ${(props) => props.theme.v4.text_l_body};
        --text_medium_body_color: ${(props) => props.theme.v4.text_m_body};

        --color_ui_4: ${(props) => props.theme.v4.ui_4};
        --color_ui_5: ${(props) => props.theme.v4.ui_5};
        --color_ui_6: ${(props) => props.theme.v4.ui_6};
        --color_ui_7: ${(props) => props.theme.v4.ui_7};
        --color_ui_9: ${(props) => props.theme.v4.ui_9};
        --color_ui_14: ${(props) => props.theme.v4.ui_14};
        --color_ui_15: ${(props) => props.theme.v4.ui_15};

        --radius_xl: ${(props) => props.theme.v4.xl_radius};
        --radius_l: ${(props) => props.theme.v4.l_radius};
        --radius_m: ${(props) => props.theme.v4.m_radius};
        --radius_s: ${(props) => props.theme.v4.s_radius};

        --bg-grey:      #FAFAFA;
        --bg-disabled:  #E2E2E2;
        --placeholder:  #8E8E93;

        --invalid:       #FF6B6B;
        --text-black:    #000000;
        --text-grey:     #8E8E93;
        --border-grey:   #F3F3F3;
        --border-input:  #F3F3F3;

        // Not used here but allows for use in custom CSS
        --color_ui_1: ${(props) => props.theme.v4.ui_1};
        --color_ui_2: ${(props) => props.theme.v4.ui_2};
        --color_ui_3: ${(props) => props.theme.v4.ui_3};
        --color_ui_8: ${(props) => props.theme.v4.ui_8};

        // Get rid of these eventually
        --core_text_ui_lg: 16px;
        --core_text_ui_lg_desktop: 16px;
        --brand: #000000;
    }

    /* Fonts */

    ${(props) => props.theme.v4.primary_font_regular && `
      @font-face {
          font-family: 'OSPrimaryFont';
          src: url(${props.theme.v4.primary_font_regular}) format('woff2');
          font-weight: normal;
          font-style: normal;
      }
    `}

    ${(props) => props.theme.v4.primary_font_bold && `
      @font-face {
          font-family: 'OSPrimaryFont';
          src: url(${props.theme.v4.primary_font_bold}) format('woff2');
          font-weight: bold;
          font-style: normal;
      }
    `}

    ${(props) => props.theme.v4.primary_font_italic && `
      @font-face {
          font-family: 'OSPrimaryFont';
          src: url(${props.theme.v4.primary_font_italic}) format('woff2');
          font-weight: normal;
          font-style: italic;
      }
    `}

    ${(props) => props.theme.v4.primary_font_bold_italic && `
      @font-face {
          font-family: 'OSPrimaryFont';
          src: url(${props.theme.v4.primary_font_bold_italic}) format('woff2');
          font-weight: bold;
          font-style: italic;
      }
    `}

    ${(props) => props.theme.v4.secondary_font_regular && `
      @font-face {
          font-family: 'OSSecondaryFont';
          src: url(${props.theme.v4.secondary_font_regular}) format('woff2');
          font-weight: normal;
          font-style: normal;
      }
    `}

    ${(props) => props.theme.v4.secondary_font_bold && `
      @font-face {
          font-family: 'OSSecondaryFont';
          src: url(${props.theme.v4.secondary_font_bold}) format('woff2');
          font-weight: bold;
          font-style: normal;
      }
    `}

    ${(props) => props.theme.v4.secondary_font_italic && `
      @font-face {
          font-family: 'OSSecondaryFont';
          src: url(${props.theme.v4.secondary_font_italic}) format('woff2');
          font-weight: normal;
          font-style: italic;
      }
    `}

    ${(props) => props.theme.v4.secondary_font_bold_italic && `
      @font-face {
          font-family: 'OSSecondaryFont';
          src: url(${props.theme.v4.secondary_font_bold_italic}) format('woff2');
          font-weight: bold;
          font-style: italic;
      }
    `}

    .material-symbols-outlined {
      font-family: 'Material Symbols Rounded';
      font-variation-settings: 'FILL' 0;

      &--filled {
        font-variation-settings: 'FILL' 1;
      }

      &--disabled {
        opacity: 0.5;
      }
    }

    .border-grey                    { border-color: var(--border-grey) }
    .disabled-bg                    { background-color: var(--bg-disabled) }

    .form-control:disabled, .form-control[readonly] {
      background-color: var(--ui_input_bg);
      opacity: 0.5;
    }

    .cursor-pointer:hover { cursor: pointer; }

    html {
        scroll-behavior: smooth;
        height: 100%;
    }

    body {
        margin: 0;
        font-family: var(--text_body_font), -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-size: 15px;
        color: var(--text_body_color);
        overflow: auto;
        overflow-x: hidden;
        line-height: 20px;
        height: 100%;
        overscroll-behavior-y: none;
    }

    #root {
        height: 100%;
    }

    .page-sites {
        min-height: 100%;
    }

    h1, h2, h3, h4, h5, h6 {
        -webkit-font-smoothing: auto;
        -moz-osx-font-smoothing: auto;
        font-family: var(--text_heading_font);
        letter-spacing: var(--text_heading_font_spacing);
        color: var(--text_heading_color);
        text-transform: var(--text_heading_font_case);
    }

    h1 {
        font-size: 24px;
        font-weight: bold;
        line-height: 32px;
    }

    h1.slogan {
      color: var(--text_slogan_color);
    }

    h1.page-header {
      margin-bottom: 0;
    }

    h2 {
        font-size: 20px;
        font-weight: bold;
        line-height: 24px;
        color: var(--text_subheading_color);
    }

    h3 {
        font-size: 16px;
        font-weight: bold;
        line-height: 24px;
        color: var(--text_subsubheading_color);
    }

    h4 {
        font-size: 16px;
        line-height: 24px;
        color: var(--text_subsubheading_color);
    }

    label {
        font-size: var(--core_text_ui_lg);
        font-weight: 400;
        @media screen and (min-width: 576px) {
          font-size: var(--core_text_ui_lg_desktop)
      }
    }

    .navbar-brand {
        cursor: pointer;
        margin: 0 auto;
        display: flex;
        padding: 0.3125rem 0;
        img {
            max-width: 100%;
        }
        @media screen and (min-width: 768px) {
            margin: 0;
        }
    }

    strong, b {
        font-weight: 600;
    }

    .badge {
        font-size: 14px;
        font-weight: 600;
        padding: 0 6px;
        margin-right: 8px;
        border-radius: var(--radius_s);
        .badge__icon {
            margin-right: 3px;
        }
    }

    .badge--menu-item, .badge--item-modal {
        padding: 0;
        font-size: 12px;
        line-height: 21px;
        text-align: center;

        &--circular {
            width: 20px;
            height: 20px;
            background-color: var(--bg-grey);
            color: #000000;
            border-radius: 50%;
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
        }
    }

    .badge--title {
        display: inline-block;
        padding: 2px 4px;
        font-size: 0;
        line-height: 0;

        &--new {
          background-color: var(--color_ui_6);
          color: #FFFFFF;
        }

        &--popular {
          background-color: var(--color_ui_14);
          color: #FFFFFF;
        }

        &--offer {
          background-color: var(--color_ui_15);
          color: #FFFFFF;
        }

        &__text {
            font-family: var(--text_body_font);
            letter-spacing: var(--text_body_font_spacing);
            text-transform: var(--text_body_font_case);
            font-size: 12px;
            line-height: 12px;
            font-weight: bold;
            vertical-align: middle;
            display: inline-block;
        }

        &__icon, &__icon.material-symbols-outlined {
            vertical-align: middle;
            display: inline-block;
            font-size: 12px;
            line-height: 12px;
            padding-right: 4px;
        }
    }


    a, button {
        touch-action: manipulation;
    }

    a, a:hover, a:active {
      color: var(--text_body_color);
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .btn-icon {
        svg {
        float: right;
        }
    }

    hr {
        border-top: 1px solid ${BORDER_COLOR}
    }


    // Forms

    .custom-select {
        border: 1px solid ${BORDER_COLOR}; // Border Colour
        border-radius: var(--radius_m);
        height: 44px;
        padding: 0 12px;
        &:focus {
            border-color: ${rgba(FOCUS_COLOR, 0.2)};
            box-shadow: 0px 0px 0px 4px ${rgba(FOCUS_COLOR, 0.15)};
        }
    }

    .separator {
        margin: 16px 0;
        border-top: 2px solid #F7F7F7;
        &.thin, &.tiny {
            border-top: 1px solid var(--border-grey);
            border-top-color: #D3D3D3;
        }
    }

    /*
    * Modals
     */

    .modal-order-backdrop, .modal-upsell-backdrop { z-index: 1040; }
    .order-modal-dialog, .upsell-modal-dialog { z-index: 1041; }

    .modal-item-backdrop { z-index: 1045; }
    .item-modal-dialog { z-index: 1046; }

    .modal-item-allergens-backdrop { z-index: 1047; }

    .modal-allergy-info-backdrop { z-index: 1048; }
    .allergy-info-modal-dialog { z-index: 1049; }

    /* Hide the upsell modal backdrop when the item modal is open to prevent a darker double backdrop */
    .modal-upsell-backdrop.show:has(~ .modal-item-backdrop.show) {
  		display: none;
	}

    .modal-header .close {
        margin: 0;
        line-height: 16px;
        border-radius: 32px;
        color: #000000;
        opacity: 1;
        margin-right: 0.5rem;
        margin-top: 0.5rem;
        padding: 1rem;
        font-weight: 500;
    }

    .modal-close__wrapper {
        position: absolute;
        top: 8px;
        right: 8px;
        height: 40px;
        width: 40px !important;
        border-radius: 50%;

        .modal-close__btn {
            border: none;
            padding: 0;
            background: transparent;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            line-height: 0;
            transition: background-color 0.15s ease-in-out;

            &:hover, &:focus {
              background: var(--bg-grey);
            }

            > span {
              font-size: 24px;
              line-height: 24px;
            }
        }
    }

    .modal-dialog {
        height: 100dvh;
        width: 100vw;
        left: 0;
        top: 0;
        overflow: hidden !important;
        display: flex !important;
        justify-content: center;
        margin: 0 !important;
        max-width: 100% !important;
        display: flex;

        @media screen and (min-width: 768px) {
            padding: 24px 16px;
        }

        opacity: 0;
        transform: scale3d(.9,.9,1);
        transition: opacity .3s ease, transform .3s ease;
        transition-delay: .2s;

        &.anim-enter {
            opacity: 1;
            transform: scaleX(1);
        }

        .modal-content {
            position: fixed;
            bottom: 0;
            left: 0;
            background: var(--color_ui_9);
            color: #000000;
            display: flex;
            flex-direction: column;
            width: 100%;
            max-height: 100%;
            border: 0;
            overflow: hidden;
            border-radius: 0;

            @media screen and (min-width: 768px) {
                position: relative;
                border-radius: var(--radius_xl);
                max-width: 560px;
                max-height: 100%;
            }
        }

        .modal-header,
        .modal-footer {
            form {
                margin: 0 !important;
            }
        }
        .modal-dialog {
            align-items: initial;
            width: 100%;
            max-width: none;
            margin: 0;
            flex-basis: 520px;
        }

        .modal-header.modal-show-header-shadow {
          border-bottom: none;
          box-shadow: 0 1px 4px rgb(0 0 0 / 8%);
        }

        .modal-footer {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: center;
            padding: 0 16px 16px 16px;
            position: relative;
            justify-content: space-between;

            &.modal-show-footer-shadow { box-shadow: 0 -1px 4px rgb(0 0 0 / 8%); }
        }
    }

    /*
    *Dropdowns
    */
    .dropdown {
    .dropdown-toggle {
        width: 100%;
        background: white !important;
        border: 1px solid #cccccc !important;
        border-radius: var(--radius_m);
        color: var(--text-black) !important;
        text-align: left;
        font-weight: 600;
        padding: 0 16px;
        height: 44px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        @media screen and (max-width: 575px) {
            font-size: 14px;
        }

        &:focus,
        &:active {
            outline: none !important;
            box-shadow: none !important;
            border: 1px solid #cccccc !important;
        }

        &::after {
            border: none;
            margin: none;
            width: 12px;
            height: 8px;
            background: url(${dropArrow});
            background-size: cover;
        }
    }
    .dropdown-menu {
        width: 100%;
        padding: 0;
        border: 1px solid #cccccc;
        border-radius: var(--radius_m);

        .dropdown-item {
            &:hover {
                background: ${BORDER_COLOR};
            }
            font-weight: 400;
            padding: 10px 16px;
            font-size: var(--core_text_ui_lg);
            @media screen and (min-width: 576px) {
                font-size: var(--core_text_ui_lg_desktop);
            }
        }
    }

    &.show {
        .dropdown-toggle {
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            &::after {
                transform: rotate(180deg);
            }
        }
        .dropdown-menu {
            border-top-left-radius: 0;
            border-top-right-radius: 0;
            transform: translate(0, 43px) !important;
        }
    }
    }

    /*
    * Form controls
    */
    .form-group {
        margin-bottom: 16px;
        label {
            font-size: var(--core_text_ui_lg);
            color: var(--text-grey);
            margin-bottom: 7px;
            @media screen and (min-width: 576px) {
              font-size: var(--core_text_ui_lg_desktop)
            }
        }
    }
    .form-control {
        border-radius: var(--radius_m);
        font-size: 16px;
        line-height: 24px;
        height: 48px;
        padding: 0 16px;
        border: var(--ui_input_border_inactive);
        box-shadow: none;
        transition: border-color 0.15s ease-in-out, outline 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        background: var(--ui_input_bg);
        color: var(--text-black);

        &:focus:not([disabled]), &.StripeElement--focus:not([disabled]) {
            background: var(--ui_input_bg);
            color: var(--text-black);
            box-shadow: none;
            border: none;
            outline: var(--ui_input_border);
        }

        &.is-invalid:not([disabled]) ,
        &.StripeElement--invalid:not([disabled])  {
            border-color: var(--invalid);
            border-width: 2px;
            background-image: none;
        }

        &.is-valid {
            background: var(--ui_input_bg);
            border: var(--ui_input_border_inactive) !important;
        }

        &.is-valid:focus:not([disabled])  {
            background: var(--ui_input_bg);
            border: var(--ui_input_border_inactive) !important;
        }

        &.is-invalid:focus:not([disabled]) , &.StripeElement--invalid.StripeElement--focus:not([disabled])  {
            border-color: 1px solid var(--invalid) !important;
        }

        &::placeholder {
            color: var(--placeholder);
        }
    }

    input[type='number'] {
        -moz-appearance:textfield;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }

    .invalid-feedback {
        font-size: var(--core_text_ui_lg);
        line-height: 24px;
        font-weight: 400;
        margin-top: 4px;
        color: var(--invalid);
        @media screen and (min-width: 576px) {
          font-size: var(--core_text_ui_lg_desktop)
        }
    }

    .input-group {
        .dropdown {
            .dropdown-toggle {
                height: 100%;
                padding: 0 12px 0 20px;
                color: ${PRIMARY_COLOR} !important;
                font-weight: 400;
                font-size: var(--core_text_ui_lg);
                @media screen and (min-width: 576px) {
                    font-size: var(--core_text_ui_lg_desktop);
                }
                border-right: none !important;

                &::after {
                    margin-left: 9px;
                }
            }
        }
    }

    .form--submitting {
        .form-control {
            pointer-events: none;
            background-color: var(--bg-grey);
        }
    }

    .page-checkout .scrolled {
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.16);
    }

    .fab__row {
        display: flex;
        justify-content: end;
        position: fixed;
        max-width: 1200px;
        bottom: 96px;
        margin: 0 auto;
        padding: 0 16px;
        width: 100%;
        pointer-events: none;

        @media screen and (min-width: 768px) {
          padding: 0;
        }
    }

    ${(props) => props.theme.v4.custom_css}

    .__PrivateStripeElement,
    .ElementsApp {
        height: 48px;
    }
`;

export default AppLayout;
